import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, MapPin, Trophy, BadgeCheck } from 'lucide-react';

const StoreCard = ({ store }) => {
  const navigate = useNavigate();
  const initials = (store.name || 'M').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div
      onClick={() => navigate(`/store/${store.id}`)}
      className="group bg-white rounded-2xl overflow-hidden cursor-pointer border border-[#1a3a2a]/10 shadow-[0_8px_32px_rgba(26,58,42,0.06)] hover:shadow-[0_16px_48px_rgba(26,58,42,0.14)] hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300"
      data-testid={`store-card-${store.id}`}
    >
      {/* Boutique plaque header */}
      <div className="relative bg-[#1a3a2a] px-4 pt-4 pb-5 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '18px 18px' }} />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4af37]/70 to-transparent" />

        <div className="relative flex items-center gap-3.5">
          {/* Gold monogram-ring avatar */}
          <div className="relative flex-shrink-0">
            <div className="w-16 h-16 rounded-full p-[2px] bg-gradient-to-br from-[#d4af37] via-[#f3e5b0] to-[#b8912a]">
              {store.avatar ? (
                <img src={store.avatar} alt={store.name} className="w-full h-full rounded-full object-cover border-2 border-[#1a3a2a]" />
              ) : (
                <div className="w-full h-full rounded-full bg-[#2c5a45] border-2 border-[#1a3a2a] flex items-center justify-center">
                  <span className="font-display text-lg text-[#f3e5b0]">{initials}</span>
                </div>
              )}
            </div>
            {store.is_founding_seller && (
              <div className="absolute -bottom-1 -right-1 bg-[#d4af37] rounded-full p-1 shadow" title="Founding Seller">
                <Trophy className="w-3 h-3 text-[#1a3a2a]" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            {store.member_since && (
              <p className="text-[9px] uppercase tracking-[0.25em] text-[#d4af37] mb-0.5">Est. {store.member_since}</p>
            )}
            <h3 className="font-display text-lg text-white leading-tight truncate flex items-center gap-1.5">
              {store.name}
              <BadgeCheck className="w-4 h-4 text-emerald-300 flex-shrink-0" />
            </h3>
            {store.location && (
              <div className="flex items-center gap-1 text-emerald-200/80 text-[11px] mt-0.5">
                <MapPin className="w-3 h-3" />
                <span className="truncate">{store.location}</span>
              </div>
            )}
          </div>

          {/* Stats column */}
          {store.product_count > 0 && (
            <div className="text-right flex-shrink-0">
              <p className="font-display text-2xl text-[#f3e5b0] leading-none">{store.product_count}</p>
              <p className="text-[9px] uppercase tracking-[0.2em] text-emerald-200/70 mt-1">Pieces</p>
            </div>
          )}
        </div>
      </div>

      {/* Product preview strip */}
      {store.sample_products && store.sample_products.length > 0 && (
        <div className="grid grid-cols-4 gap-1 p-1.5 bg-[#f0ede6]">
          {store.sample_products.slice(0, 4).map((product, idx) => (
            <div key={idx} className="aspect-square overflow-hidden rounded-lg bg-white">
              {product.images?.[0] ? (
                <img
                  src={product.images[0]}
                  alt={product.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="font-display text-xs text-[#a8a29e]">Minted</span>
                </div>
              )}
            </div>
          ))}
          {Array.from({ length: Math.max(0, 4 - (store.sample_products?.length || 0)) }).map((_, idx) => (
            <div key={`empty-${idx}`} className="aspect-square rounded-lg bg-white/60" />
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-3">
        {store.description && (
          <p className="text-[#57534e] text-xs line-clamp-2 mb-2.5 leading-relaxed">{store.description}</p>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 text-xs text-[#57534e]">
            <span className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 text-[#d4af37] fill-[#d4af37]" />
              {store.rating > 0 ? store.rating.toFixed(1) : 'New'}
              {store.review_count > 0 && <span className="text-[#a8a29e]">({store.review_count})</span>}
            </span>
            {store.sales_count > 0 && (
              <span className="text-[#a8a29e]">· {store.sales_count} sold</span>
            )}
            {store.top_category && (
              <span className="hidden sm:inline-block px-2 py-0.5 rounded-full bg-[#1a3a2a]/5 text-[#1a3a2a] text-[10px] uppercase tracking-wider">{store.top_category}</span>
            )}
          </div>
          <span className="text-xs font-medium text-[#1a3a2a] relative after:absolute after:left-0 after:-bottom-0.5 after:h-px after:w-0 after:bg-[#d4af37] group-hover:after:w-full after:transition-all after:duration-300">
            Visit Boutique →
          </span>
        </div>
      </div>
    </div>
  );
};

export default StoreCard;
